/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as Crud from 'src/@crud';
import useManageCrud from 'src/@manage/use-manage.js';
import { addClasses, cn } from '#lib/helpers/class.js';
import { LoadingIndicator } from 'lib/components/loading.jsx';
import { useRouteParams } from 'src/hooks/use-route-params.js';
import { useForm } from 'lib/hooks/use-form.js';
import Planner from '#@planner/planner/planner.jsx';
import Places from '#@itinerary/places/places.jsx';
import { usePlanner } from '#@planner/planner/use-planner.js';
import { usePlaceFilters } from '#@itinerary/places/reducers/use-place-filters.jsx';
import { validatePlanner } from '#helpers/helpers.js';
import { useProfilesQueries } from '#hooks/use-itinerary-options.js';
import { useFilters } from '#hooks/use-filters.js';
import { useDocuments } from '#@itinerary/documents/use-documents.js';
import { initializeDayId } from '#@planner/planner/helpers.js';
import { useMedia } from '#hooks/use-media.js';
// import RangeGraph from '#components/range-graph.jsx';
import { usePlaces } from '#@itinerary/places/reducers/use-places.jsx';
import { group, ungroup } from '#@itinerary/places/helpers.js';
import useAuth from '#lib/hooks/use-auth.jsx';
import { usePublish } from '../../../@itinerary/use-publish.js';
import { Header } from '../../../@itinerary/header/header.jsx';
import { Media } from '#@itinerary/media/media.jsx';
import { Documents } from '#@itinerary/documents/documents.jsx';
import { ActionButtons } from '#@itinerary/actions/action-buttons.jsx';
import { NotificatorChannel } from '#@itinerary/notifications/notificator-channel.jsx';
import { SectionHeading } from '#components/headings.jsx';
import { ItineraryPreview } from '#@itinerary/itinerary-preview.jsx';
import toast from 'react-hot-toast';
import useBlockNavigation from '#hooks/use-block-navigation.js';
import UnsavedChangesModal from '#components/unsaved-changes-modal.jsx';
import { SlideOverDiff } from '#@itinerary/panel-diff/slide-over-diff.jsx';
import { useSlideOverDiff } from '#@itinerary/panel-diff/use-slide-over-diff.js';
import { Prices } from '#@itinerary/prices/prices.jsx';
import { useCurrencies } from '#hooks/use-currencies.js';
import { validateHeader } from './custom-validator.js';
import { useThemes } from '#hooks/use-themes.js';
import { UserContext } from '#src/index.jsx';
import useItineraryAiDescription from '#hooks/use-itinerary-ai-description.js';
import { useItineraryTags } from '#@itinerary/header/tags/use-itinerary-tags.js';
import { capitalize } from '#lib/utils/string.js';

export const ItineraryContext = React.createContext();

export default function ItineraryEdit(routeProps) {
    const { id } = useRouteParams(routeProps);
    const { isCreate } = Crud.useRouteParams();
    const { user } = useAuth();
    const { getLanguage } = useContext(UserContext);
    const lang = getLanguage();
    const percentageTypes = [
        {
            id: 1,
            value: 1,
            name: 'MARKUP'
        },
        {
            id: 2,
            value: 2,
            name: 'MARGIN'
        }
    ];
    // BLOCKER
    // block navigation
    const { proceedNavigation, blocked, reset, setUnsavedChanges } = useBlockNavigation();

    // NOTIFICATOR CHANNEL
    const notificatorChannel = `TripsAround_${user?.username}`;

    const [isSavingBeforePublish, setIsSavingBeforePublish] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [isSavingBeforeTranslation, setIsSavingBeforeTranslation] = useState(false);
    const [showTranslationLoading, setShowTranslationLoading] = useState(false);
    const [hiddenSave, setHiddenSave] = useState(false);

    useEffect(() => {
        if (isSavingBeforePublish || isSavingBeforeTranslation) return setHiddenSave(true);
        return setHiddenSave(false);
    }, [hiddenSave, isSavingBeforePublish, isSavingBeforeTranslation]);

    let crud = useManageCrud(
        'itinerary',
        { id },
        {
            init: true,
            create: {
                onError: (error) => {
                    toast.error('Validation error');
                    validatePlanner(error.cause, days, crud?.data?.places, setPlannerError);
                }
            },
            update: {
                onAfterUpdate: () => {
                    setIsSavingBeforePublish(false);
                    setIsSavingBeforeTranslation(false);
                },
                onError: (error) => {
                    toast.error(`${error.message}`);
                    validatePlanner(error.cause, days, crud?.data?.places, setPlannerError);
                    setIsSavingBeforePublish(false);
                    setShowTranslationLoading(false);
                },
                hiddenSave: hiddenSave
            },
            clone: {
                key: ['itinerary', id]
            }
        }
    );

    // VALIDATE
    //  is an array of objects {day: '', plan: '', field: '', room: '', place: ''}
    const [plannerError, setPlannerError] = useState(null);
    // funzione per rimuovere un errore dall'oggetto
    // se non viene passato nessuno oggetto gli errori vengono resettati
    const clearPlannerError = useCallback(
        ({ dayId, planId, placeId, field }) => {
            // const updated = plannerError?.filter(
            //     (el) =>
            //         (el.day !== dayId || el.plan !== planId || el.field !== field) &&
            //         (el.place !== placeId || el.field !== field)
            // );
            setPlannerError(null /*  [...updated] */);
        },
        [plannerError, setPlannerError]
    );

    // INIT FORM
    const form = useForm({
        initialData: crud.data,
        setUnsavedChanges,
        onSubmit: (data) => {
            const errors = validateHeader({ title, startDate: data.startDate }, setHeaderError);
            if (errors) return;
            if (!data.percentageType) data.percentageType = 1;
            saveItinerary({ data });
        }
    });

    const [headerError, setHeaderError] = useState([]);
    const clearHeaderError = (name) => {
        if (name) setHeaderError((prev) => prev.filter((el) => el !== name));
    };

    // SAVE
    const saveItinerary = ({ data, newPlaces, newPlaceFilters }) => {
        const ungroupedPlaces = ungroup(newPlaces || places);
        const imagesLoading =
            ungroupedPlaces.flatMap((up) => up.gallery).some((i) => i?.isUploading) ||
            plans.flatMap((p) => p.gallery).some((i) => i?.isUploading);
        if (
            documents.some((doc) => doc.isUploading) ||
            mediaIntro.data.isUploading ||
            mediaJingle.data.isUploading ||
            mediaCover.data.isUploading ||
            imagesLoading
        ) {
            toast.error(_t.crud.itinerary.headers.waitMediaLoaded());
            return;
        }

        if (id) data.id = id;
        data.documents = [...documents];
        data.title = title;
        data.description = description;
        data.assignedTo = assignedTo;
        data.intro = intro;
        data.jingle = jingle;
        data.cover = cover;
        data.currency = currency;
        data.enableTotalPrice = enableTotalPrice;
        data.enablePlanPrice = enablePlanPrice;
        data.percentage = Number(percentage) || 0;
        data.totalPrice = Number(totalPrice) || 0;
        data.percentageType = percentageTypes.find((el) => el.name === percentageType)?.id ?? 1;
        data.tags = tags;

        const itineraryPlaceFilters = newPlaceFilters || placeFiltersProps.placeFilters;
        data.placeFilters = itineraryPlaceFilters.filter((el) => el.name);

        crud.save.mutate({
            ...data,
            clients,
            days,
            tasks,
            feedbacks,
            places: ungroupedPlaces
        });
        setUnsavedChanges();
    };

    // HEADER INFO
    const {
        title,
        description,
        startDate,
        endDate,
        compactAccommodation,
        compactDescriptions,
        groupLeader,
        emergencyNumber,
        email,
        phoneNumber,
        assignedTo,
        pnr,
        // notificationsEmail,
        connectionId,
        // feedbacks,
        currency,
        percentageType: percentageTypeId,
        enableTotalPrice,
        enablePlanPrice,
        totalPrice,
        percentage,
        diffJson
    } = form.data;

    const percentageType = percentageTypes.find((el) => el.id == percentageTypeId ?? 1)?.name;
    const [assignedToName, setAssignedToName] = useState('');
    const [clients, setClients] = useState(crud?.data?.clients || []);
    const [tasks, setTasks] = useState(structuredClone(crud?.data?.tasks) || []);
    const [feedbacks, setFeedbacks] = useState(structuredClone(crud?.data?.feedbacks) || []);
    useEffect(() => setClients(crud?.data?.clients || []), [crud.data]);
    useEffect(() => setTasks(crud?.data?.tasks || []), [crud.data]);
    useEffect(() => setFeedbacks(crud?.data?.feedbacks || []), [crud.data]);
    const { itineraryOptions } = useProfilesQueries();

    const activeProfile =
        itineraryOptions?.find((v) => v.id === form?.data.activeProfile) ||
        itineraryOptions?.find((v) => v.isDefault) ||
        itineraryOptions?.[0] ||
        {};

    // PROPS PER LA SELECT DEI PROFILI
    const profileProps = form.register('activeProfile');
    const handleChangeProfile = (value) => {
        form.handleChange('activeProfile', value);
        const profile = itineraryOptions?.find((v) => v.id === value);
        if (!profile) return;
        let update;
        const diff = checkIfProfileHasDiff({ intro, jingle, cover }, profile);
        if (diff) {
            update = confirm(_t.edit.confirmUpdateMedia());
        }
        if (update) {
            profile.defaultIntro && setIntro(() => ({ ...profile.defaultIntro, sourceType: 'I' }));
            profile.defaultJingle && setJingle(() => ({ ...profile.defaultJingle, sourceType: 'I' }));
            profile.defaultCover && setCover(() => ({ ...profile.defaultCover, sourceType: 'I' }));
        }
    };
    profileProps.onChange = handleChangeProfile;

    const languageProps = form.register('language');
    const language = languageProps.value;
    // GESTIONE LINGUE DEL PROFILO
    const { themes } = useThemes();
    const [selectedTheme, setSelectedTheme] = useState(null);
    const [supportedLanguages, setSupportedLanguages] = useState([]);

    useEffect(() => {
        if (!themes || !themes.length || !activeProfile) return;
        const profileThemeId = activeProfile?.styles?.themeId;
        const theme = themes.find((t) => t.id === profileThemeId);
        setSelectedTheme(theme);
    }, [themes, activeProfile]);

    useEffect(() => {
        if (!selectedTheme) return;
        const languages = JSON.parse(selectedTheme.defaultStyles).supportedLanguages;
        setSupportedLanguages(languages);
    }, [selectedTheme]);

    //----------------------------------

    // PUBLISH
    const { onUpdateGuid, setIsLoadingPublish, ...publishProps } = usePublish({
        itineraryGuid: crud.data?.guid,
        itineraryId: id
    });

    // STATO PER GESTIRE MODALE SHARE ITINERARY
    const [isOpen, setIsOpen] = useState(false);
    const onOpen = () => setIsOpen(true);
    const onClose = () => setIsOpen(false);

    //-----------------------------------------

    // MEDIA
    const mediaIntro = useMedia({
        initialData: crud.data?.intro,
        setUnsavedChanges
    });
    const mediaJingle = useMedia({
        initialData: crud.data?.jingle,
        setUnsavedChanges
    });
    const mediaCover = useMedia({
        initialData: crud.data?.cover,
        setUnsavedChanges
    });

    const [intro, setIntro] = useState(mediaIntro.data ? mediaIntro.data?.meta : crud.data?.intro);
    const [cover, setCover] = useState(mediaCover.data ? mediaCover.data?.meta : crud.data?.cover);
    const [jingle, setJingle] = useState(mediaJingle.data ? mediaJingle.data?.meta : crud.data?.jingle);

    useEffect(() => {
        if (mediaIntro.data) setIntro(() => mediaIntro.data.meta);
        if (mediaJingle.data) setJingle(() => mediaJingle.data.meta);
        if (mediaCover.data) setCover(() => mediaCover.data.meta);
    }, [mediaIntro.data, mediaCover.data, mediaJingle.data]);

    let jingleFile;
    if (jingle) {
        jingleFile = new Audio();
        jingleFile.src = jingle.src;
    }

    function checkIfProfileHasDiff(former, current) {
        if (Object.values(former).every((value) => !value) && Object.values(current).every((value) => !value))
            return false;
        let intro, jingle, cover;
        if (former.intro?.src !== current.defaultIntro?.src) intro = true;
        if (former.jingle?.src !== current.defaultJinglejingle?.src) jingle = true;
        if (former.cover?.src !== current.defaultCover?.src) cover = true;
        return intro || jingle || cover;
    }

    //-----------------------------------------

    // PLANNER
    const plannerProps = usePlanner({
        data: crud.data,
        init: initializeDayId,
        setUnsavedChanges
    });
    const days = plannerProps.days;
    const edit = plannerProps.edit;

    const plans = days.flatMap((day, dayIndex) =>
        day.plans?.map((plan, index) => {
            return {
                type: plan?.type,
                planIndex: index,
                dayIndex: dayIndex,
                ...plan
            };
        })
    );

    //-----------------------------------------

    // DOCUMENTS
    const {
        data: documents,
        isActive: isDocumentActive,
        onAdd: onAddDocument,
        onDelete: onDeleteDocument,
        onEdit: onEditDocument,
        setActiveDocument,
        getActiveDocument
    } = useDocuments({
        initialData: crud.data?.documents,
        setUnsavedChanges
    });

    const handleDeleteDocument = (index) => {
        const plans = days.flatMap((day) => day.plans);
        plans.forEach((plan) => {
            if (!plan.documents) return;
            const newDocs = plan.documents.filter((doc) => doc !== index);
            edit(['', plan.id], 'documents', newDocs);
        });
        onDeleteDocument(index);
    };
    const [showDocumentModal, setShowDocumentModal] = useState(false);

    //-----------------------------------------

    // filter management
    const { totalFilters, updatePlaces, ...filtersProps } = useFilters({ onError: () => setIsPlacesCreating(false) });
    const placeFiltersProps = usePlaceFilters(crud.data?.placeFilters);
    const [isPlacesCreating, setIsPlacesCreating] = useState(false);

    // per gestire un eventuale errore di generazione dei places
    useEffect(() => {
        if (filtersProps.isError) setIsPlacesCreating(false);
    }, [filtersProps.isError]);

    const placesProps = usePlaces(crud.data?.places, group, setUnsavedChanges);
    const { places, editPlace, updateStateWithFromNotification } = placesProps;

    const onEdit = ({ resultData, planData, multiAction }) => {
        // if (!multiAction) edit([planData.dayId, planData.planId], planData.field, planData?.value);
        if (resultData)
            editPlace(resultData.name, resultData.type, resultData?.field, resultData.data, resultData.id, multiAction);
        if (planData) edit([planData.dayId, planData.planId], planData.field, planData?.value, multiAction);
    };

    //-----------------------------------------

    // GET CURRENCIES
    const { currencies } = useCurrencies();

    //IFRAME
    const [showItineraryPreview, setShowItineraryPreview] = useState(window.innerWidth > 1700);
    const [anchorPlan, setAnchorPlan] = useState('');
    const previewData = useMemo(() => {
        const newPercentageType = percentageTypes.find((el) => el.name == (percentageType ?? 'MARKUP')).id;
        console.log(newPercentageType);
        return {
            id,
            guid: crud.data?.guid,
            publishedLink: publishProps.publishedLink,
            title,
            description,
            language,
            startDate,
            endDate,
            compactAccommodation,
            compactDescriptions,
            groupLeader,
            emergencyNumber,
            assignedTo: assignedToName,
            email,
            phoneNumber,
            currency: currency,
            totalPrice: Number(totalPrice),
            percentage: Number(percentage),
            percentageType: Number(newPercentageType),
            enablePlanPrice,
            enableTotalPrice,
            documents,
            days,
            places: ungroup(places) ?? [],
            cover: mediaCover.data?.meta,
            jingle: mediaJingle.data?.meta,
            intro: mediaIntro.data?.meta
        };
    }, [
        id,
        crud,
        publishProps,
        title,
        description,
        language,
        startDate,
        endDate,
        compactAccommodation,
        compactDescriptions,
        groupLeader,
        emergencyNumber,
        email,
        phoneNumber,
        documents,
        days,
        mediaIntro,
        mediaJingle,
        mediaCover,
        places,
        currencies,
        currency,
        totalPrice,
        percentage,
        percentageType,
        enablePlanPrice,
        enableTotalPrice
    ]);

    //-----------------------------------------

    // REFRESH ITINERARY TO MANAGE DIFF
    const currentHeader = {
        title: title ?? '',
        description: description ?? '',
        startDate: startDate ?? '',
        endDate: endDate ?? '',
        // compactAccommodation: compactAccommodation ?? '',
        groupLeader: groupLeader ?? '',
        // emergencyNumber: emergencyNumber ?? '',
        email: email ?? '',
        phoneNumber: phoneNumber ?? '',
        pnr: pnr ?? '',
        // notificationsEmail: notificationsEmail ?? '',
        cover: cover ?? '',
        jingle: jingle ?? '',
        intro: intro ?? '',
        currency: currency ?? '',
        totalPrice: totalPrice ?? 0
    };

    // States per gestire la visualizzazione del diff
    const [updatedHeader, setUpdatedHeader] = useState();
    const [updatedDocuments, setUpdatedDocuments] = useState([]);
    const [updatedPlaces, setUpdatedPlaces] = useState([]);

    useEffect(() => {
        if (
            title ||
            description ||
            startDate ||
            endDate ||
            // compactAccommodation ||
            groupLeader ||
            // emergencyNumber ||
            email ||
            phoneNumber ||
            pnr ||
            // notificationsEmail ||
            currency ||
            totalPrice
        ) {
            setUpdatedHeader({ ...currentHeader });
        }
        if (documents) {
            setUpdatedDocuments(documents);
        }
        if (places) setUpdatedPlaces(ungroup(places));
        return;
    }, [
        title,
        description,
        startDate,
        endDate,
        // compactAccommodation,
        groupLeader,
        // emergencyNumber,
        email,
        phoneNumber,
        pnr,
        // notificationsEmail,
        documents,
        places,
        currency,
        totalPrice,
        cover,
        intro,
        jingle
    ]);

    // PLANNER
    const { days: updatedDays, ...updatedPlannerProps } = usePlanner({ data: days, setUnsavedChanges });

    let updatedPlans = [];
    if (Array.isArray(updatedDays))
        updatedPlans = updatedDays?.flatMap((day, dayIndex) =>
            day.plans.map((plan, index) => {
                return {
                    type: plan?.type,
                    planIndex: index,
                    dayIndex: dayIndex,
                    ...plan
                };
            })
        );

    const { incomingData, handleUpdateItinerary, refreshLoading, ...slideOverDiffProps } = useSlideOverDiff({
        id,
        connectionId,
        crud,
        data: form.data,
        // dati del modello attuale
        currentHeader,
        currentDays: days,
        currentPlans: plans,
        currentDocuments: documents,
        // dati aggiornati durante il diff
        updatedHeader,
        updatedDocuments,
        updatedDays,
        updatedPlaces,
        // setter per aggiornare i dati del modello,
        setUpdatedHeader,
        setUpdatedDocuments,
        setUpdatedPlaces,
        replaceUpdatedDays: updatedPlannerProps.replaceState,
        onUpdateAll: (model) => {
            crud.save.mutate(model);
        },
        currentPlaces: places
    });

    //-----------------------------------------

    // AI ITINERARY DESCRIPTION

    const aiMutation = useItineraryAiDescription({
        onSuccess: (res) => {
            if (!res.result) return toast.error(_t.edit.aiDescriptionGenerationError());
            toast.success(_t.edit.aiDescriptionGenerationSuccess());
            form.handleChange('description', res.result);
        }
    });

    const handleGenerateDescription = (alert) => {
        if (alert && !confirm(_t.edit.itineraryAiDescriptionWarning())) return;
        const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
        const languageId = language || 'en';
        const languageName = languageNames.of(languageId);
        aiMutation.mutate({ id, language: { id: languageId, name: languageName } });
    };

    // Tag Management
    // Manage tags
    const { tags, associateTag, deleteTag } = useItineraryTags(id);

    //-----------------------------------------
    if (crud.isLoading || !crud.data || !Object.keys(form.data).length) {
        return (
            <LoadingIndicator
                className={addClasses('z-10 absolute top-0 left-0 w-full h-full justify-center items-center')}
            />
        );
    }

    return (
        <ItineraryContext.Provider
            value={{
                ...form?.data,
                days,
                plans,
                ...plannerProps,
                documents,
                places,
                feedbacks,
                setFeedbacks,
                tasks,
                setTasks,
                clients,
                setClients,
                assignedToName,
                tags,
                associateTag,
                deleteTag,
                activeProfile,
                themeId: activeProfile?.styles?.themeId,
                profileLanguage: language || activeProfile.language || 'en',
                intlLang: lang
            }}
        >
            <main className="flex w-full overflow-x-hidden border-none">
                {crud.isLoading && (
                    <LoadingIndicator
                        className={addClasses('z-10 absolute top-0 left-0 w-full h-full justify-center items-center')}
                    />
                )}
                {crud.isSuccess && (
                    <React.Fragment>
                        <div
                            className={cn(
                                'grid grid-cols-12 border-none transition-all',
                                showItineraryPreview ? 'w-2/3' : 'w-full'
                            )}
                            style={{ maxWidth: '2000px' }}
                        >
                            <Header
                                crud={crud}
                                form={form}
                                id={id}
                                supportedLanguages={supportedLanguages}
                                profileProps={profileProps}
                                languageProps={languageProps}
                                handleAssignedToName={setAssignedToName}
                                itineraryOptions={itineraryOptions}
                                headerError={headerError}
                                clearError={clearHeaderError}
                                diffModel={diffJson ? JSON.parse(diffJson) : null}
                                showTranslationLoading={showTranslationLoading}
                                setShowTranslationLoading={setShowTranslationLoading}
                                isSaving={isSavingBeforeTranslation ?? false}
                                setIsSaving={setIsSavingBeforeTranslation}
                                aiMutation={aiMutation}
                                handleGenerateDescription={handleGenerateDescription}
                                setIsTranslating={setIsTranslating}
                            />
                            <div className="grid grid-cols-1 pl-4 md:pl-8 pt-2 md:pt-16 pr-9 bg-white col-span-full gap-x-5 gap-y-6 md:ml-[275px]">
                                <Media
                                    form={form}
                                    intro={intro}
                                    mediaIntro={mediaIntro}
                                    jingleFile={jingleFile}
                                    mediaJingle={mediaJingle}
                                    cover={cover}
                                    mediaCover={mediaCover}
                                />
                                <Prices
                                    form={form}
                                    currency={currency}
                                    percentageType={percentageType}
                                    percentageTypes={percentageTypes}
                                    symbol={
                                        currencies?.find((el) => el.id === currency || el.name === currency)?.symbol
                                    }
                                    currencies={currencies}
                                    enableTotalPrice={enableTotalPrice}
                                    enablePlanPrice={enablePlanPrice}
                                    totalPrice={totalPrice}
                                    plans={plans}
                                    onEditPlan={edit}
                                />
                                <Planner
                                    itineraryId={id}
                                    places={places}
                                    editPlaces={onEdit}
                                    filters={totalFilters}
                                    plans={plans}
                                    documents={documents}
                                    handleAnchor={setAnchorPlan}
                                    plannerError={plannerError}
                                    handleError={clearPlannerError}
                                    setUnsavedChanges={setUnsavedChanges}
                                    date={startDate}
                                    endDate={endDate}
                                    enablePlanPrice={enablePlanPrice}
                                    currencies={currencies}
                                    currency={currency}
                                    totalFilters={totalFilters}
                                    showItineraryPreview={showItineraryPreview}
                                    profileLanguage={activeProfile.language}
                                    {...plannerProps}
                                />
                                <Documents
                                    form={form}
                                    documents={documents}
                                    setActiveDocument={setActiveDocument}
                                    isDocumentActive={isDocumentActive}
                                    onEditDocument={onEditDocument}
                                    onDeleteDocument={handleDeleteDocument}
                                    onAddDocument={onAddDocument}
                                    getActiveDocument={getActiveDocument}
                                    showDocumentModal={showDocumentModal}
                                    setShowDocumentModal={setShowDocumentModal}
                                />

                                {/* <RangeGraph
                                    days={days}
                                    plans={plans.filter((plan) => plan.type === 'accommodation')}
                                    handleEdit={edit}
                                    handleMove={movePlan}
                                /> */}
                                {id && (
                                    <section className="flex flex-col col-span-full">
                                        <SectionHeading id="handlePlaces" subtext={_t.edit.placesDescription()}>
                                            {_t.edit.placesText()}
                                        </SectionHeading>
                                        <Places
                                            itineraryId={id}
                                            placeFiltersProps={placeFiltersProps}
                                            placesProps={placesProps}
                                            updatePlaces={updatePlaces}
                                            filtersProps={filtersProps}
                                            isPlacesCreating={isPlacesCreating}
                                            setIsPlacesCreating={setIsPlacesCreating}
                                            totalFilters={totalFilters}
                                            plans={plans}
                                            addPlanFromPlace={plannerProps.addPlanFromPlace}
                                            totalDays={days.length}
                                            hasError={plannerError}
                                            handleError={clearPlannerError}
                                            setUnsavedChanges={setUnsavedChanges}
                                            formData={form?.data}
                                            saveItinerary={saveItinerary}
                                            language={language}
                                        />
                                    </section>
                                )}
                            </div>
                        </div>
                        <ItineraryPreview
                            previewData={previewData}
                            activeProfile={activeProfile}
                            anchorPlan={anchorPlan}
                            showItineraryPreview={showItineraryPreview}
                            onOpenPreview={() => setShowItineraryPreview(true)}
                            onClosePreview={() => setShowItineraryPreview(false)}
                        />
                        {/* Actions & Notifications */}
                        <ActionButtons
                            crud={crud}
                            form={form}
                            id={id}
                            isCreate={isCreate}
                            isImported={!!connectionId}
                            isOpen={isOpen}
                            onClose={onClose}
                            onOpen={onOpen}
                            onUpdateItinerary={handleUpdateItinerary}
                            refreshLoading={refreshLoading}
                            lastPublish={crud?.data?.lastPublish}
                            isSaving={isSavingBeforePublish ?? false}
                            setIsSaving={setIsSavingBeforePublish}
                            activeProfile={activeProfile}
                            handleGenerateDescription={handleGenerateDescription}
                            description={description}
                            {...publishProps}
                        />
                        <NotificatorChannel
                            notificatorChannel={notificatorChannel}
                            isTranslating={isTranslating}
                            setIsTranslating={setIsTranslating}
                            isPlacesCreating={isPlacesCreating}
                            setIsPlacesCreating={setIsPlacesCreating}
                            setIsLoadingPublish={setIsLoadingPublish}
                            onUpdateGuid={onUpdateGuid}
                            onUpdatePlaces={updateStateWithFromNotification}
                            title={title}
                            crud={crud}
                            itineraryId={id}
                            edit={edit}
                            setShowTranslationLoading={setShowTranslationLoading}
                            languageProps={languageProps}
                        />
                        {incomingData && (
                            <SlideOverDiff
                                form={form}
                                // dati del modello attuale
                                currentHeader={currentHeader}
                                currentDocuments={documents}
                                currentDays={days}
                                currentPlans={plans}
                                // dati aggiornati durante il diff
                                updatedHeader={updatedHeader}
                                setUpdatedHeader={setUpdatedHeader}
                                updatedDocuments={updatedDocuments}
                                setUpdatedDocuments={setUpdatedDocuments}
                                updatedDays={updatedDays}
                                updatedPlans={updatedPlans}
                                updatedPlaces={updatedPlaces}
                                setUpdatedPlaces={setUpdatedPlaces}
                                currentPlaces={places}
                                totalFilters={totalFilters}
                                {...updatedPlannerProps}
                                {...slideOverDiffProps}
                            />
                        )}
                    </React.Fragment>
                )}
                <UnsavedChangesModal isOpen={blocked} onClose={reset} onConfirm={proceedNavigation} />
            </main>
        </ItineraryContext.Provider>
    );
}
